import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, Truck, RotateCcw, Info, X, Trash2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { SITE_URL, buildBreadcrumbLd } from "@/lib/seo";
import { useSeoT, PAGE_META } from "@/lib/seoPagesI18n";
import { useToolsT } from "@/lib/toolsI18n";
import { ROOM_KEYS, objectsForRoom, vehicleForVolume, customVolumeM3, VOLUME_MARGIN } from "@/lib/toolsConfig";
import { useProject, getVolumeTotal, getVolumeByRoom, getVolumeItemCount, track } from "@/lib/projectStore";

const H2 = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export default function CalculateurVolume() {
  const navigate = useNavigate();
  const { t: st, lang } = useSeoT();
  const T = useToolsT(lang);
  const meta = (PAGE_META[lang] || PAGE_META.fr).calculateur;
  const ct = st.calc;
  const { project, update, reset } = useProject(true);
  const [activeRoom, setActiveRoom] = useState(ROOM_KEYS[0]);
  const [customForm, setCustomForm] = useState(null); // {name,l,w,h,qty}

  React.useEffect(() => { track("volume_calculator_start"); }, []);

  const qty = project?.volume?.qty || {};
  const custom = project?.volume?.custom || [];
  const total = getVolumeTotal(project);
  const byRoom = getVolumeByRoom(project);
  const count = getVolumeItemCount(project);
  const veh = vehicleForVolume(total);

  const bc = [{ name: st.bc.home, path: "/" }, { name: st.bc.outils, path: "/outils" }, { name: ct.h1, path: "/outils/calculateur-volume-demenagement" }];
  const key = (id) => `${activeRoom}::${id}`;
  const setQ = (id, d) => {
    update((p) => {
      const k = key(id);
      const next = Math.max(0, (p.volume.qty[k] || 0) + d);
      if (next === 0) delete p.volume.qty[k]; else p.volume.qty[k] = next;
      return p;
    });
    if (d > 0) track("volume_item_add", { item: id });
  };
  const addCustom = () => {
    if (!customForm?.name) return;
    update((p) => {
      p.volume.custom.push({ id: `c_${Date.now().toString(36)}`, room: activeRoom, name: customForm.name, l: customForm.l, w: customForm.w, h: customForm.h, qty: Math.max(1, Number(customForm.qty) || 1) });
      return p;
    });
    track("volume_item_add", { item: "custom" });
    setCustomForm(null);
  };
  const removeCustom = (id) => update((p) => { p.volume.custom = p.volume.custom.filter((c) => c.id !== id); return p; });

  const objects = useMemo(() => objectsForRoom(activeRoom), [activeRoom]);

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "WebApplication", name: ct.h1, applicationCategory: "UtilitiesApplication", operatingSystem: "Web", url: `${SITE_URL}/outils/calculateur-volume-demenagement`, offers: { "@type": "Offer", price: "0", priceCurrency: "CHF" }, publisher: { "@id": `${SITE_URL}/#organization` } },
    buildBreadcrumbLd(toLdItems(bc)),
  ];
  const roomsWithVol = Object.entries(byRoom).filter(([, v]) => v > 0);

  const goQuote = () => { track("volume_calculator_complete", { volume: total }); navigate(`/devis?service=move&volume=${total}`); };

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="calc-volume">
      <Seo title={meta.title} description={meta.desc} path="/outils/calculateur-volume-demenagement" jsonLd={jsonLd} />
      <Header />
      <section className="relative bg-[#0A0F2C] text-white overflow-hidden pt-28 md:pt-36 pb-12 md:pb-16 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.4), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1080px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-3xl md:text-5xl tracking-tighter leading-[1.02] max-w-2xl" style={H2}>{ct.h1}</h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl">{ct.heroSub}</p>
        </div>
      </section>

      <section className="py-8 md:py-14 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-[1.5fr_1.15fr] gap-8 lg:gap-12" style={{ maxWidth: "1120px" }}>
          <div className="flex flex-col gap-6">
            {/* Room tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="calc-room-tabs">
              {ROOM_KEYS.map((rk) => (
                <button key={rk} onClick={() => setActiveRoom(rk)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-all ${activeRoom === rk ? "bg-[#2D5BFF] text-white" : "bg-[#F7F7F8] text-[#4B5563] hover:bg-gray-200"}`} data-testid={`calc-room-${rk}`}>
                  {T.rooms[rk]}{byRoom[rk] > 0 ? ` · ${byRoom[rk]}m³` : ""}
                </button>
              ))}
            </div>

            {/* Objects for active room */}
            <div className="flex flex-col divide-y divide-gray-100 rounded-2xl border border-gray-100">
              {objects.map((it) => {
                const label = T.objects[it.id] || it.id;
                const q = qty[key(it.id)] || 0;
                return (
                  <div key={it.id} className="flex items-center justify-between gap-4 p-3.5" data-testid={`calc-item-${it.id}`}>
                    <div><div className="font-semibold text-[15px]">{label}</div><div className="text-xs text-[#9CA3AF]">≈ {it.v} m³</div></div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setQ(it.id, -1)} className="w-9 h-9 rounded-full bg-[#F7F7F8] hover:bg-gray-200 inline-flex items-center justify-center" aria-label={`- ${label}`} data-testid={`calc-minus-${it.id}`}><Minus size={16} /></button>
                      <span className="w-6 text-center font-bold tabular-nums" data-testid={`calc-qty-${it.id}`}>{q}</span>
                      <button onClick={() => setQ(it.id, 1)} className="w-9 h-9 rounded-full bg-[#2D5BFF] text-white hover:bg-blue-600 inline-flex items-center justify-center" aria-label={`+ ${label}`} data-testid={`calc-plus-${it.id}`}><Plus size={16} /></button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Custom objects in this room */}
            {custom.filter((c) => c.room === activeRoom).length > 0 && (
              <div className="rounded-2xl border border-gray-100 divide-y divide-gray-100">
                {custom.filter((c) => c.room === activeRoom).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-4 p-3.5" data-testid={`calc-custom-${c.id}`}>
                    <div><div className="font-semibold text-[15px]">{c.name}</div><div className="text-xs text-[#9CA3AF]">≈ {Math.round(customVolumeM3(c.l, c.w, c.h, c.qty) * 100) / 100} m³ · ×{c.qty}</div></div>
                    <button onClick={() => removeCustom(c.id)} className="w-9 h-9 rounded-full bg-red-50 text-red-500 hover:bg-red-100 inline-flex items-center justify-center" aria-label="delete" data-testid={`calc-custom-del-${c.id}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Add custom */}
            {customForm ? (
              <div className="rounded-2xl bg-[#F7F7F8] p-5" data-testid="calc-custom-form">
                <div className="flex items-center justify-between mb-3"><div className="font-bold text-sm">{T.volume.addCustom}</div><button onClick={() => setCustomForm(null)} aria-label="close"><X size={18} /></button></div>
                <input value={customForm.name} onChange={(e) => setCustomForm({ ...customForm, name: e.target.value })} placeholder={T.volume.customName} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm mb-3" data-testid="calc-custom-name" />
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <input type="number" value={customForm.l} onChange={(e) => setCustomForm({ ...customForm, l: e.target.value })} placeholder={T.volume.length} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="calc-custom-l" />
                  <input type="number" value={customForm.w} onChange={(e) => setCustomForm({ ...customForm, w: e.target.value })} placeholder={T.volume.width} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="calc-custom-w" />
                  <input type="number" value={customForm.h} onChange={(e) => setCustomForm({ ...customForm, h: e.target.value })} placeholder={T.volume.height} className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="calc-custom-h" />
                </div>
                <div className="flex items-center gap-3">
                  <input type="number" min="1" value={customForm.qty} onChange={(e) => setCustomForm({ ...customForm, qty: e.target.value })} className="w-20 rounded-xl border border-gray-200 px-3 py-2.5 text-sm" data-testid="calc-custom-qty" />
                  <button onClick={addCustom} className="flex-1 rounded-full bg-[#2D5BFF] text-white px-5 py-2.5 font-bold text-sm" data-testid="calc-custom-add">{T.common.add}</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setCustomForm({ name: "", l: "", w: "", h: "", qty: 1 })} className="self-start inline-flex items-center gap-2 rounded-full bg-[#EAF0FF] text-[#2D5BFF] px-5 py-2.5 font-bold text-sm hover:bg-[#dbe6ff] transition-colors" data-testid="calc-add-custom-btn"><Plus size={15} /> {T.volume.addCustom}</button>
            )}
          </div>

          {/* Result panel */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-3xl bg-[#F7F7F8] p-8">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{T.volume.estimated}</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-extrabold text-6xl tabular-nums leading-none" data-testid="calc-total">{total}</span>
                <span className="font-bold text-xl text-[#6B7280]">m³</span>
              </div>
              <p className="mt-3 text-xs text-[#9CA3AF] leading-relaxed">{T.volume.margin} · {count} {T.volume.itemsCount.toLowerCase()}</p>

              {roomsWithVol.length > 0 && (
                <div className="mt-7" data-testid="calc-byroom">
                  <div className="text-xs font-bold text-[#6B7280] mb-3">{T.volume.byRoom}</div>
                  <div className="flex flex-col gap-2.5">
                    {roomsWithVol.map(([rk, v]) => (
                      <div key={rk} className="flex items-center justify-between text-sm"><span>{T.rooms[rk] || rk}</span><span className="font-bold tabular-nums">{v} m³</span></div>
                    ))}
                  </div>
                </div>
              )}

              {veh ? (
                <div className="mt-7 rounded-2xl bg-white p-5 flex items-center gap-4" data-testid="calc-vehicle">
                  <div className="w-11 h-11 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] inline-flex items-center justify-center shrink-0"><Truck size={19} /></div>
                  <div><div className="text-[11px] text-[#9CA3AF] font-bold uppercase tracking-wide">{T.volume.vehicleTitle}</div><div className="font-bold text-sm mt-0.5">{T.veh[veh.key]}</div></div>
                </div>
              ) : (
                <p className="mt-7 text-sm text-[#6B7280] leading-relaxed">{T.volume.empty}</p>
              )}

              <button onClick={goQuote} className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#1a2050] text-white px-6 py-4 font-extrabold text-sm transition-all whitespace-nowrap" data-testid="calc-cta-quote">{T.volume.cta} <ArrowRight size={15} /></button>
              <button onClick={() => { reset(); }} className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#6B7280] hover:text-[#0A0F2C]" data-testid="calc-reset"><RotateCcw size={14} /> {T.common.reset}</button>
              <p className="mt-6 flex items-start gap-2 text-[11px] text-[#9CA3AF] leading-relaxed"><Info size={13} className="shrink-0 mt-0.5" /> {T.volume.disclaimer}</p>
            </div>

            {/* Maillage interne */}
            <div className="mt-6 rounded-3xl border border-gray-100 p-7">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{T.volume.relatedTitle}</div>
              <div className="mt-4 flex flex-col gap-3 text-sm font-semibold">
                <Link to="/guides/calcul-volume-demenagement" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.volumeGuide} <ArrowRight size={13} /></Link>
                <Link to="/services/demenagement" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.moving} <ArrowRight size={13} /></Link>
                <Link to="/outils/inventaire-demenagement" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.checklist} <ArrowRight size={13} /></Link>
                <Link to="/outils" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.tools} <ArrowRight size={13} /></Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
